/**
 * Accès au domaine Activité (`ACT-01` → `ACT-18`).
 *
 * Les durées et distances partent en **texte** : le serveur normalise `44:12`, `8,40`,
 * `1h30` (`ACT-01`). Le client ne convertit rien — un second analyseur finirait par
 * diverger du premier.
 */

import type { Readiness } from '@/features/body/api';
import type { SwimLine } from '@/features/planning/api';
import { request } from '@/lib/api';

export interface Run {
  id: number;
  token: string;
  date: string;
  distance_km: number;
  duration_min: number;
  pace_min_km: number | null;
  speed_kmh: number | null;
  avg_hr: number | null;
  elevation_m: number | null;
  /** Pas par minute. Rien ne la déduit : elle est saisie ou lue sur une capture. */
  cadence_spm: number | null;
  note: string | null;
  source: string;
  /** Identifiant stable, celui auquel les paliers se rattachent. Vide sur une saisie. */
  run_id: string;
  /** Kilocalories **actives** : la dépense de la séance seule. */
  active_calories: number | null;
  /** Kilocalories **totales**, métabolisme de base compris — jamais « calories » tout court. */
  total_calories: number | null;
  /** `19:40:00`, telle que le serveur la rend. Situe la séance, ne la date pas. */
  start_time: string | null;
  end_time: string | null;
  split_length_km: number | null;
  /** Nombre de paliers relevés. Zéro sur une course saisie au clavier, pas une erreur. */
  splits: number;
  /**
   * Chemin du `.fit` importé (`docs/import-fit.md`). Vide sur une saisie ou une capture.
   *
   * L'écran ne s'en sert que comme d'un **drapeau** : non vide, il propose le fichier et
   * va chercher le tracé. Il ne le construit jamais en adresse — les deux routes se
   * désignent par la course, et le chemin ne quitte pas le serveur autrement que comme
   * ce booléen déguisé.
   */
  fit_path: string;
  /** La plus haute FC tenue, lue dans le `.fit`. `null` sans cardio. */
  max_hr: number | null;
  /** L'effort perçu, de 1 à 10 — saisi après la sortie (`docs/coach-course.md`, **C10**). */
  rpe: number | null;
}

// ── Analyse d'une sortie importée (`docs/analyse-course.md`) ──

/** La couleur d'un tronçon contre l'allure en mouvement — décidée par le serveur. */
export type PaceClass = 'faster' | 'even' | 'slower';

/**
 * Un point de la grille en distance. La courbe le dessine, le tracé le colore, et les
 * deux se répondent parce qu'ils partagent ce même point.
 *
 * `x` et `y` sont **déjà normalisés** entre 0 et 1 : aucune latitude ni longitude.
 */
export interface RunPoint {
  distance_km: number;
  /** Temps de chrono auquel le point a été atteint, pauses retirées. */
  timer_s: number;
  pace_min_km: number | null;
  pace_class: PaceClass | null;
  heart_rate: number | null;
  cadence_spm: number | null;
  power_w: number | null;
  /** `null` sauf relief : sur du plat, le profil ne dessinerait que le bruit. */
  altitude_m: number | null;
  x: number | null;
  y: number | null;
}

export interface RunStop {
  /** `pause` : chrono arrêté, sans effet sur l'allure. `stop` : chrono en marche. */
  kind: 'pause' | 'stop';
  distance_km: number;
  duration_s: number;
}

/**
 * Un constat **rédigé par le serveur**. L'écran affiche `title` et `text` tels quels et
 * teinte selon `tone` ; il ne décide sur rien d'autre.
 */
export interface RunInsight {
  code: string;
  tone: 'good' | 'bad' | 'neutral';
  title: string;
  text: string;
}

export interface RunEffort {
  distance_m: number;
  label: string;
  duration_s: number;
  pace_min_km: number;
  start_km: number;
  /** Bat tous les efforts des autres sorties à cette distance. */
  record: boolean;
}

export interface RunZoneBin {
  zone: number;
  name: string;
  /** Les bornes, déjà écrites : « 5:59–6:47 /km ». */
  range: string;
  seconds: number;
  share: number;
}

export interface RunZones {
  kind: 'heart_rate' | 'pace';
  reference_value: number;
  /** `watch` : le réglage de la montre, en attendant une mesure — ni saisie ni mesure. */
  source: 'settings' | 'deduced' | 'watch';
  /** D'où vient la référence, en toutes lettres. Une estimation le dit toujours. */
  detail: string;
  bins: RunZoneBin[];
  summary: string;
}

export interface RunLoadWeek {
  start: string;
  load: number;
  runs: number;
  /** Part de la semaine la plus lourde, entre 0 et 1 — servie, pas cherchée. */
  share: number;
}

/** La charge d'entraînement, et ce qu'elle dit (`docs/coach-course.md` §6). */
export interface RunLoad {
  acute: number | null;
  chronic_weekly: number | null;
  ratio: number | null;
  ratio_text: string;
  easy_share: number | null;
  moderate_share: number | null;
  hard_share: number | null;
  /** Les trois parts en entiers qui font 100 (plus grand reste, au serveur). */
  easy_pct: number | null;
  moderate_pct: number | null;
  hard_pct: number | null;
  distribution_text: string;
  last_hard: string | null;
  unmeasured: number;
  weeks: RunLoadWeek[];
}

/**
 * Un facteur et ce qu'il dit. `shown` : un écart a passé l'épreuve ; `none` : rien de net ;
 * `pending` : pas assez de sorties. Le texte est rédigé par le serveur.
 */
export interface RunCorrelation {
  key: string;
  label: string;
  status: 'shown' | 'none' | 'pending';
  text: string;
  n_high: number;
  n_low: number;
  effect_pct: number | null;
}

export interface RunTrends {
  load: RunLoad;
  correlations: RunCorrelation[];
}

export interface RunWeather {
  temperature_c: number | null;
  apparent_c: number | null;
  humidity_pct: number | null;
  dew_point_c: number | null;
  wind_kmh: number | null;
}

/**
 * Ce qui entourait une sortie (`docs/coach-course.md` §4). Chaque partie est `null` quand
 * elle manque — pas de météo sans réponse d'Open-Meteo, pas de forme sans mesure du matin.
 */
export interface RunConditions {
  rpe: number | null;
  weather: RunWeather | null;
  morning: Readiness | null;
  /** Ce qui entourait la sortie, en phrases rédigées par le serveur. */
  context: string[];
  /**
   * Ce que la sortie pèse dans la charge globale (M-02, point 4) : mesurée par l'effort
   * perçu (`rpe`), estimée depuis la FC (`hr`), ou hors de la charge (`null`). La phrase est
   * celle du serveur ; vide pour un doublon.
   */
  global_load: number | null;
  global_load_source: 'rpe' | 'hr' | null;
  global_load_text: string;
}

/** Découplage et efficacité, échauffement retiré (`docs/coach-course.md`). */
export interface RunAerobic {
  basis: 'power' | 'pace';
  decoupling_pct: number;
  efficiency: number;
  heart_rate_first: number;
  heart_rate_second: number;
}

/**
 * Ce que la montre a calculé elle-même. **Signé à l'écran** : ni mesure de Metric, ni
 * proposition d'un modèle. Un champ que Garmin ne documente pas n'arrive qu'une fois sa
 * lecture confirmée — `null` sinon.
 */
export interface RunGarmin {
  device: string | null;
  vo2max: number | null;
  training_effect_aerobic: number | null;
  training_effect_aerobic_label: string | null;
  training_effect_anaerobic: number | null;
  training_effect_anaerobic_label: string | null;
  training_load: number | null;
  recovery_h: number | null;
  performance_condition_start: number | null;
  performance_condition_end: number | null;
  stamina_start_pct: number | null;
  stamina_end_pct: number | null;
}

export interface RunStrideMeasure {
  average: number;
  /** Dernier tiers moins premier tiers, dans l'unité de la mesure. */
  change: number | null;
}

/** La foulée. `wrist` : estimée au poignet, sans capteur de poitrine. */
export interface RunStride {
  source: 'sensor' | 'wrist';
  cadence_spm: RunStrideMeasure | null;
  step_length_m: RunStrideMeasure | null;
  stance_ms: RunStrideMeasure | null;
  vertical_oscillation_cm: RunStrideMeasure | null;
  vertical_ratio_pct: number | null;
}

export interface RunAnalysis {
  points: RunPoint[];
  located: boolean;
  width: number;
  height: number;
  distance_ticks_km: number[];
  /** **Le plus lent d'abord** : l'axe arrive retourné. */
  pace_domain_min_km: [number, number] | null;
  pace_ticks_min_km: number[];
  heart_rate_domain: [number, number] | null;
  cadence_domain: [number, number] | null;
  power_domain: [number, number] | null;
  altitude_domain_m: [number, number] | null;
  average_pace_min_km: number | null;
  /** Présente seulement quand des arrêts chrono en marche l'écartent de la moyenne. */
  moving_pace_min_km: number | null;
  paused_s: number;
  stopped_s: number;
  stops: RunStop[];
  /** Le seuil des couleurs du tracé, en s/km — cité par la légende. */
  class_threshold_s: number;
  insights: RunInsight[];
  efforts: RunEffort[];
  zones: RunZones | null;
  /** Ce que coûte le prochain geste quand les zones manquent. */
  zones_missing: string | null;
  aerobic: RunAerobic | null;
  garmin: RunGarmin | null;
  stride: RunStride | null;
  average_power_w: number | null;
  normalized_power_w: number | null;
}

/**
 * Un palier d'une course (`ACT-19`).
 *
 * `partial` est le champ qui porte tout : sur 8,14 km, la neuvième ligne fait `00:44` et
 * n'est **pas** un kilomètre. Son allure est une extrapolation de l'application, pas une
 * mesure — l'écran la grise, et n'en fait aucune moyenne.
 */
export interface RunSplit {
  index: number;
  duration_s: number;
  /** La longueur réelle — 1 pour un plein, le reliquat pour un partiel. */
  distance_km: number | null;
  pace_min_km: number | null;
  cadence_spm: number | null;
  avg_hr: number | null;
  elevation_m: number | null;
  partial: boolean;
  /** Part de la barre de cadence, servie par le serveur : aucun `Math.max` à l'écran. */
  cadence_ratio: number | null;
  /** L'autre lecture de l'allure, pour qui lit en km/h. */
  speed_kmh: number | null;
  /** Mètres par pas — `distance ÷ (cadence × durée)`. Absente sans cadence relevée. */
  stride_m: number | null;
  /** Écart à l'allure moyenne des paliers pleins. **Négatif = plus rapide.** */
  delta_s_per_km: number | null;
  /**
   * Part **signée** de la barre divergente, entre -1 et 1.
   *
   * Le signe porte le côté et la valeur absolue la longueur : l'écran ne cherche ni
   * maximum ni sens dans la liste — les deux seraient un calcul métier sur une collection
   * de mesures.
   */
  deviation_ratio: number | null;
  /** Écart à la cadence moyenne, en pas par minute, et sa barre signée. */
  cadence_delta_spm: number | null;
  cadence_deviation_ratio: number | null;
}

export interface RunSplits {
  splits: RunSplit[];
  full_count: number;
  partial_count: number;
  /**
   * Secondes par kilomètre, seconde moitié moins première.
   *
   * **Négatif veut dire plus rapide**, ce qui se lit à l'envers du signe : l'écran le dit
   * en toutes lettres plutôt que de montrer un `-4,2` que rien n'explique.
   */
  drift_s_per_km: number | null;
  first_half_pace_min_km: number | null;
  second_half_pace_min_km: number | null;
  fastest_index: number | null;
  slowest_index: number | null;
  /** Bornes de l'axe d'allure, **le plus lent d'abord** : l'axe arrive déjà retourné. */
  pace_domain_min_km: [number, number] | null;
  cadence_max_spm: number | null;

  /**
   * Allure de référence des paliers pleins, celle contre laquelle les écarts se mesurent.
   * Elle diffère de l'allure de la course, qui inclut le reliquat.
   */
  average_pace_min_km: number | null;
  fastest_pace_min_km: number | null;
  slowest_pace_min_km: number | null;
  pace_spread_s_per_km: number | null;
  /** Écart-type des allures, en s/km — la régularité de la course en une valeur. */
  pace_sd_s_per_km: number | null;
  negative_split: boolean | null;

  cadence_avg_spm: number | null;
  cadence_min_spm: number | null;
  /**
   * **Positive = la foulée s'accélère** — le signe inverse de celui de `drift_s_per_km`.
   * Deux sens opposés pour deux dérives : l'écran nomme chacun en toutes lettres.
   */
  cadence_drift_spm: number | null;
  stride_avg_m: number | null;
  stride_min_m: number | null;
  stride_max_m: number | null;
  deviation_max_s_per_km: number | null;
  cadence_deviation_max_spm: number | null;
}

/** Une course de l'historique, réduite à ce qu'une courbe de tendance en montre. */
export interface RunMark {
  id: number;
  date: string;
  distance_km: number;
  pace_min_km: number | null;
  /** Celle qu'on regarde. Le serveur la désigne — l'écran ne compare pas d'identifiants. */
  current: boolean;
}

/**
 * Ce que l'historique dit de cette course-ci.
 *
 * **Un rang n'est pas un classement absolu.** Comparer l'allure d'un 8 km à celle d'un
 * 3 km est bancal : `runs_compared` accompagne toujours le rang, pour que l'écran écrive
 * « 2ᵉ sur 14 » et non « 2ᵉ ». Vide quand il n'y a qu'une course — une première sortie ne
 * se compare à rien, et un rang de 1 sur 1 ressemblerait à un record.
 */
export interface RunContext {
  runs_compared: number;
  pace_rank: number | null;
  distance_rank: number | null;
  best_pace_min_km: number | null;
  longest_distance_km: number | null;
  average_pace_min_km: number | null;
  average_distance_km: number | null;
  /** Écart de cette course aux moyennes. Négatif sur l'allure = plus rapide. */
  pace_delta_s_per_km: number | null;
  distance_delta_km: number | null;
  /** Les dernières sorties, la plus ancienne d'abord. */
  recent: RunMark[];
  /** Bornes de l'axe de tendance, le plus lent d'abord — servies, jamais dérivées ici. */
  pace_domain_min_km: [number, number] | null;
}

/**
 * Une bande de distance et son meilleur temps (`ACT-20`).
 *
 * **La seule comparaison d'allures honnête de la page.** 5'30" sur 15 km est une meilleure
 * course que 5'10" sur 3 km ; à l'intérieur d'une bande, les sorties se ressemblent assez
 * pour qu'un record veuille dire quelque chose.
 */
export interface DistanceBand {
  label: string;
  runs: number;
  best_pace_min_km: number | null;
  /** La course qui détient le record — l'écran y mène, il ne la cherche pas. */
  best_index: number | null;
  best_day: string | null;
  average_pace_min_km: number | null;
  total_distance_km: number;
}

/** Un mois de course. C'est ici que « progresser » se lit sans réserve à poser. */
export interface MonthTotals {
  /** `2026-08`. L'écran le met en forme, il ne le calcule pas. */
  month: string;
  runs: number;
  distance_km: number;
  minutes: number;
  pace_min_km: number | null;
}

/** Les N dernières sorties contre les N précédentes. `size` à 0 = trop peu pour comparer. */
export interface RunWindow {
  size: number;
  recent_pace_min_km: number | null;
  previous_pace_min_km: number | null;
  /** Secondes par kilomètre, récent moins précédent. **Négatif = plus rapide.** */
  pace_delta_s_per_km: number | null;
  recent_distance_km: number | null;
  previous_distance_km: number | null;
  distance_delta_km: number | null;
}

/** La page « Toutes tes courses » : la liste et ce qu'elle raconte, en une réponse. */
export interface RunProgress {
  /** Toutes les courses, la plus récente d'abord. */
  runs: Run[];
  total_runs: number;
  total_distance_km: number;
  total_minutes: number;
  /** Distance totale sur temps total — et non la moyenne des allures. */
  overall_pace_min_km: number | null;
  best_pace_min_km: number | null;
  best_pace_index: number | null;
  best_pace_day: string | null;
  longest_distance_km: number | null;
  longest_distance_index: number | null;
  longest_distance_day: string | null;
  longest_duration_min: number | null;
  bands: DistanceBand[];
  /** Du plus ancien au plus récent. Les mois sans course sont **absents**. */
  months: MonthTotals[];
  window: RunWindow;
  /** Bornes d'allure, **le plus lent d'abord** : l'axe arrive retourné. */
  pace_domain_min_km: [number, number] | null;
  volume_domain_km: [number, number] | null;
  /** Bornes de distance, le plus court d'abord — l'abscisse du nuage de points. */
  distance_domain_km: [number, number] | null;
  /** Le meilleur temps de l'historique sur chaque distance, la plus courte d'abord. */
  records: EffortRecord[];
  /** Les distances courues au moins deux fois, et leur progression. */
  effort_series: EffortSeries[];
  /** Sorties `.fit` d'avant les efforts : tant qu'il en reste, l'écran propose de réanalyser. */
  efforts_pending: number;
  /** Les dernières semaines, **vides comprises**, la plus ancienne d'abord. */
  weeks: RunWeek[];
  week_domain_km: [number, number] | null;
}

export interface EffortRecord {
  distance_m: number;
  label: string;
  duration_s: number;
  pace_min_km: number;
  day: string;
  /** La position de la course, pour y mener. */
  run: number;
  /** Combien de sorties couvrent cette distance. */
  runs: number;
}

export interface EffortMark {
  day: string;
  run: number;
  duration_s: number;
  pace_min_km: number;
  /** Battait, ce jour-là, tout ce qui précédait. */
  record: boolean;
}

export interface EffortSeries {
  distance_m: number;
  label: string;
  marks: EffortMark[];
  /** Le plus lent d'abord. */
  pace_domain_min_km: [number, number] | null;
}

export interface RunWeek {
  /** Le lundi, tel que le serveur le date. */
  week: string;
  runs: number;
  distance_km: number;
  minutes: number;
}

export interface EffortRebuild {
  runs: number;
  efforts: number;
  splits: number;
}

/** Une course et ses paliers. `run` à `null` = aucune course, ce qui n'est pas une panne. */
export interface RunDetail {
  run: Run | null;
  splits: RunSplits;
  context: RunContext;
}

export interface ActivityItem {
  /**
   * `workout` désigne une **séance tabata déclarée faite**, plus une séance de
   * musculation saisie à la main : celle-ci n'existe plus. Le mot du fil est resté celui
   * de l'historique fusionné, où il distingue « ce qui n'est pas une course ».
   */
  kind: 'run' | 'workout' | 'swim' | 'other';
  /** La position de la ligne dans son fichier — `runs.csv`, `circuit_sessions.csv` ou `swims.csv`. */
  id: number;
  token: string;
  date: string;
  label: string;
  duration_min: number;
  distance_km: number | null;
  pace_min_km: number | null;
  rpe: number | null;
  /** Les **rounds** d'une séance tabata. Zéro sur une course, qui n'en a pas. */
  entries: number;
  source: string;
  /** Une natation : ses mètres, et l'identifiant stable qui ouvre sa feuille. */
  distance_m: number | null;
  swim_id: string | null;
}

export interface DayVolume {
  date: string;
  weekday: number;
  minutes: number;
  rest: boolean;
}

export interface WeekTotals {
  week_start: string;
  minutes: number;
  sessions: number;
  distance_km: number;
  pace_min_km: number | null;
}

export interface WeekVolume {
  week_start: string;
  minutes: number;
  sessions: number;
}

export interface NeglectedGroup {
  muscle_group: string;
  days_since: number | null;
  last_date: string | null;
}

export interface ActivityOverview {
  /** Le jour, dans le fuseau du serveur. L'écran ne consulte pas l'horloge du téléphone. */
  today: string;
  week: WeekTotals;
  days: DayVolume[];
  weeks: WeekVolume[];
  neglected: NeglectedGroup[];
  history: ActivityItem[];
  total: number;
}

export interface RunPayload {
  date: string;
  duration_min: string;
  distance_km?: string | null;
  /** `5:16` ou `5,27` — même écriture qu'une durée. */
  pace_min_km?: string | null;
  avg_hr?: string | null;
  elevation_m?: string | null;
  cadence_spm?: string | null;
  note?: string | null;
}

/** L'en-tête de garde d'une écriture destructrice (`STO-05`). */
function guard(token: string): Record<string, string> {
  return { 'If-Match': token };
}

// ── Circuits ouverts dans Cadence Tabata ──────────────

export interface CircuitExercise {
  position: number;
  name: string;
  muscle_group: string;
  duration_s: number | null;
  reps: number | null;
  rest_s: number;
  /** Ce qui a été **saisi** sur cet exercice. Vide et jamais `null` : c'est un champ. */
  note: string;
  /**
   * Ce que le **lien** porte en 4ᵉ champ — la charge et la note, déjà composées.
   *
   * `null` quand il n'y a ni l'une ni l'autre. Servi plutôt que recomposé
   * ici : il n'y a qu'un endroit au monde où « 12 » devient « 12 kg » et où les deux se
   * joignent, et c'est celui qui fabrique le lien. Deux compositions divergeraient, et la
   * carte annoncerait autre chose que ce que la séance affichera.
   */
  link_note: string | null;
}

export interface Circuit {
  id: number;
  token: string;
  circuit_id: string;
  name: string;
  rounds: number;
  round_rest_s: number;
  created: string | null;
  note: string | null;
  exercises: CircuitExercise[];
  /**
   * L'adresse à ouvrir, **déjà construite par le serveur**.
   *
   * `null` tant que l'adresse de Cadence n'est pas réglée. L'échappement, le bornage et le
   * suffixe qui distingue quinze répétitions de quinze secondes sont des règles métier :
   * l'écran pose ce qu'il reçoit dans un `href`, il ne fabrique rien.
   */
  url: string | null;
  estimated_duration_min: number;
  /** Faux dès qu'un exercice est en répétitions : le total s'affiche alors préfixé d'un `~`. */
  exact: boolean;
}

/**
 * Un nom d'exercice proposé à la saisie d'un circuit.
 *
 * **Servi par le serveur, jamais écrit ici.** Cadence embarque 1324 démonstrations : les
 * recopier ici mettrait 70 ko dans le paquet de l'application et les ferait diverger du
 * jour où elle en ajoute une. Le serveur cherche, l'écran affiche.
 */
export interface CircuitSuggestion {
  name: string;
  /** Le groupe musculaire quand l'exercice est au catalogue de Metric, `null` sinon. */
  muscle_group: string | null;
  /** Zone du corps et matériel quand la suggestion vient du catalogue de Cadence. */
  body_part: string | null;
  equipment: string | null;
}

export interface CircuitList {
  circuits: Circuit[];
  /**
   * Vrai quand une adresse de Cadence est réglée.
   *
   * **Non déductible de la liste** : sur une liste vide, l'écran doit distinguer « aucun
   * circuit » de « aucune adresse », et ces deux états ne proposent pas le même geste.
   */
  linkable: boolean;
}

export interface CircuitExercisePayload {
  name: string;
  muscle_group: string;
  duration_s?: number;
  reps?: number;
  rest_s: number;
  /** Ce qu'on veut lire sous le nom pendant l'effort. Le serveur y joint la charge. */
  note: string;
}

export interface CircuitPayload {
  name: string;
  rounds: number;
  round_rest_s: number;
  exercises: CircuitExercisePayload[];
  note?: string | null;
}

export interface CircuitDonePayload {
  duration_min: number;
  rpe?: number | null;
}

/** Une série d'un exercice, dans une séance déclarée faite. */
export interface CircuitSessionSet {
  exercise_name: string;
  muscle_group: string;
  sets: number;
  /** `null` sur un exercice au temps : la sentinelle du fichier ne sort pas de l'API. */
  reps: number | null;
}

/**
 * Un circuit **déclaré fait**, tel que le serveur le rend.
 *
 * Elle a remplacé `Workout` dans la réponse de « je l'ai fait » : la séance de
 * musculation manuelle n'existe plus, et rendre l'ancien schéma aurait obligé l'écran à
 * lire un `type` et un `workout_id` qui ne désignent plus rien.
 */
export interface CircuitSession {
  id: number;
  token: string;
  session_id: string;
  circuit_id: string;
  date: string;
  /** Le nom du circuit au moment où il a été fait (`ACT-06`). */
  name: string;
  rounds: number;
  duration_min: number;
  rpe: number | null;
  source: string;
  sets: CircuitSessionSet[];
}

// ── Charges des exercices de tabata ───────────────────

/**
 * L'état d'une charge, **décidé par le serveur**.
 *
 * Trois valeurs et non deux : « pas encore renseigné » n'est pas « poids du corps ». La
 * page groupe ses sections sur cette étiquette ; la déduire d'un `weight_kg` à `null`
 * reviendrait à tenir ici une règle qui vit là-bas.
 */
export type LoadState = 'unset' | 'bodyweight' | 'weighted';

/** Un exercice **proposé** par la composition assistée — ajustable, jamais écrit. */
export interface ProposedCircuitExercise {
  name: string;
  muscle_group: string;
  duration_s: number | null;
  reps: number | null;
  rest_s: number;
  /**
   * Vrai quand le nom est exactement celui d'un exercice du catalogue Cadence, donc quand
   * une démonstration s'affichera pendant l'effort.
   *
   * Faux **n'est pas une erreur** : un nom hors catalogue reste valide et la séance
   * tourne. C'est l'écran qui le dit, pour qu'on choisisse de corriger ou non.
   */
  illustrated: boolean;
}

export interface CircuitProposal {
  name: string;
  rounds: number;
  round_rest_s: number;
  exercises: ProposedCircuitExercise[];
  /** Ce sur quoi la proposition s'appuie — matériel pris en compte, groupes les plus anciens. */
  basis: string[];
  /** Exercices écartés à la relecture, et pourquoi. */
  dropped: string[];
}

export interface Load {
  /**
   * `null` tant qu'aucune charge n'a été déclarée : il n'y a alors **aucune ligne**, donc
   * ni position ni jeton. C'est ce couple à `null` qui dit à l'écran de créer plutôt que
   * de corriger.
   */
  id: number | null;
  token: string | null;
  name: string;
  state: LoadState;
  weight_kg: number | null;
  updated: string | null;
  /** Nombre de séances tabata qui emploient cet exercice. */
  circuits: number;
  /**
   * Jours depuis le dernier **changement** de charge, lu au journal des décisions.
   *
   * `null` quand le journal ne porte rien — jamais `0`, qui voudrait dire « changée
   * aujourd'hui ».
   */
  days_since_change: number | null;
  /**
   * Séances tenues à cette charge depuis ce changement.
   *
   * `0` est une mesure : « montée il y a trois jours, aucune séance depuis ». C'est `null`
   * qui dit qu'il n'y a rien depuis quoi compter.
   */
  sessions_since: number | null;
}

export interface LoadList {
  loads: Load[];
  /**
   * Les charges déclarées pour un exercice qui n'est **plus dans aucun circuit**.
   *
   * Servies à part de `loads` : on ne demande pas de renseigner un exercice qu'on ne
   * joue plus. Mais les taire les rendait invisibles — et donc indestructibles, puisque
   * aucun écran ne pouvait viser leur ligne. `circuits` y vaut toujours `0`.
   */
  orphans: Load[];
  /** Le pas des boutons plus et moins, servi plutôt que codé ici. */
  step_kg: number;
}

export interface LoadPoint {
  date: string;
  /**
   * `null` quand ce point est un passage au poids du corps : la courbe s'y **interrompt**
   * plutôt que de retomber à zéro, qui serait une charge nulle.
   */
  weight_kg: number | null;
}

export interface LoadDay {
  date: string;
  /** Séances de ce jour portant cet exercice. Zéro est une mesure, pas une absence. */
  count: number;
}

export interface LoadDetail {
  name: string;
  state: LoadState;
  weight_kg: number | null;
  /** Les décisions de charge, dans l'ordre. Vient du journal des changements. */
  history: LoadPoint[];
  /** **Exactement 30 entrées**, la dernière étant le jour du serveur. */
  sessions: LoadDay[];
  circuits: string[];
  /**
   * L'adresse du GIF de démonstration, servi par l'instance Cadence de l'utilisateur.
   *
   * `null` dans trois cas — pas d'adresse de base réglée, instance injoignable, nom sans
   * correspondance exacte au catalogue — et l'écran ne les distingue pas : aucun n'appelle
   * un geste différent, et « démonstration indisponible » ferait passer pour une panne
   * l'état normal d'un exercice écrit à la main.
   *
   * Metric ne relaie aucun octet : le navigateur va chercher l'image là où elle est déjà
   * servie. L'adresse porte donc la clé d'accès de la base.
   */
  demo_url: string | null;
}

export interface LoadPayload {
  name: string;
  weight_kg?: number;
  bodyweight?: boolean;
}

/** La sortie contre la séance prévue ce jour-là (`docs/accueil-et-charge.md`, CA-04). */
export interface RunAdherence {
  planned: boolean;
  source: 'coach' | 'plan' | null;
  title: string | null;
  judged_km: number;
  in_range_km: number;
  basis: 'pace' | 'hr' | null;
  target: string | null;
  text: string;
  caveat: string | null;
  /** Une course planifiée en notation, jugée bloc par bloc sur la trace du `.fit`. */
  blocks: { label: string; measured: string; target: string; ok: boolean | null }[];
  judged_blocks: number;
  in_range_blocks: number;
}

export const activityApi = {
  overview: () => request<ActivityOverview>('/api/activity'),
  muscleGroups: () => request<string[]>('/api/activity/muscle-groups'),

  createRun: (payload: RunPayload) =>
    request<Run>('/api/activity/runs', { method: 'POST', body: payload }),
  readRun: (id: number) => request<Run>(`/api/activity/runs/${id}`),
  latestRun: () => request<RunDetail>('/api/activity/runs/latest'),
  runProgress: () => request<RunProgress>('/api/activity/runs/progress'),
  runSplits: (id: number) => request<RunDetail>(`/api/activity/runs/${id}/splits`),
  updateRun: (id: number, token: string, payload: RunPayload) =>
    request<Run>(`/api/activity/runs/${id}`, {
      method: 'PATCH',
      headers: guard(token),
      body: payload,
    }),
  deleteRun: (id: number, token: string) =>
    request<undefined>(`/api/activity/runs/${id}`, { method: 'DELETE', headers: guard(token) }),
  /** L'effort perçu seul, sous `If-Match`. `null` l'efface. */
  setRunRpe: (id: number, token: string, rpe: number | null) =>
    request<Run>(`/api/activity/runs/${id}/rpe`, {
      method: 'PUT',
      headers: guard(token),
      body: { rpe },
    }),
  runConditions: (id: number) => request<RunConditions>(`/api/activity/runs/${id}/conditions`),
  runTrends: () => request<RunTrends>('/api/activity/runs/trends'),

  /**
   * Importe une sortie depuis un `.fit`. **Écrit directement** (`docs/import-fit.md`,
   * **F5**) : il n'y a pas de brouillon à faire relire parce qu'il n'y a rien à deviner.
   */
  importFit: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<Run>('/api/activity/runs/fit', { method: 'POST', form });
  },
  /**
   * Importe une natation en piscine depuis son `.fit` (`docs/planning-natation.md` §6).
   *
   * Appelé quand l'import de course répond `fit_is_swim` : l'écran **réaiguille sur le
   * code**, jamais sur le message, et l'utilisateur n'a pas à savoir qu'il y a deux routes.
   */
  importSwimFit: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<SwimDetail>('/api/activity/swims/fit', { method: 'POST', form });
  },
  swim: (swimId: string) => request<SwimDetail>(`/api/activity/swims/${swimId}`),
  /** Les natations, importées et saisies, et les mètres des quatre dernières semaines. */
  swims: () => request<SwimList>('/api/activity/swims'),
  /**
   * Saisit une natation **dans la notation** (`docs/natation-activite.md`). La distance
   * n'est pas envoyée : c'est le total que le serveur lit dans le programme.
   */
  createSwim: (payload: SwimPayload) =>
    request<SwimDetail>('/api/activity/swims', { method: 'POST', body: payload }),
  /** Corrige une natation **saisie** — une importée se corrige au tour. */
  updateSwim: (id: number, token: string, payload: SwimPayload) =>
    request<SwimDetail>(`/api/activity/swims/${String(id)}`, {
      method: 'PATCH',
      headers: guard(token),
      body: payload,
    }),
  /** Corrige un tour : distance, temps (`1:45`), nage — seul ce qui est envoyé change, et la
   *  séance reprend ses totaux de ses tours (`docs/natation-corriger.md`). */
  editSwimLap: (
    id: number,
    token: string,
    body: { distance_m?: number; duration?: string; stroke?: string },
  ) =>
    request<SwimDetail>(`/api/activity/swims/laps/${String(id)}/edit`, {
      method: 'PATCH',
      headers: guard(token),
      body,
    }),
  /** Recolle un tour et le suivant — une longueur que la montre a coupée. */
  mergeSwimLap: (id: number, token: string) =>
    request<SwimDetail>(`/api/activity/swims/laps/${String(id)}/merge`, {
      method: 'POST',
      headers: guard(token),
    }),
  /** Retire un tour — une pause prise pour une longueur. */
  deleteSwimLap: (id: number, token: string) =>
    request<SwimDetail>(`/api/activity/swims/laps/${String(id)}`, {
      method: 'DELETE',
      headers: guard(token),
    }),
  /** Date, heure, bassin, note — rien de mesuré, sur toute natation. */
  updateSwimMeta: (
    id: number,
    token: string,
    body: { date: string; start: string | null; pool_m: number; note: string | null },
  ) =>
    request<SwimDetail>(`/api/activity/swims/${String(id)}/meta`, {
      method: 'PATCH',
      headers: guard(token),
      body,
    }),
  /** Corrige la nage d'un tour. `null` rend la lecture de la montre. */
  correctSwimLap: (id: number, token: string, stroke: string | null) =>
    request<SwimDetail>(`/api/activity/swims/laps/${String(id)}`, {
      method: 'PATCH',
      headers: guard(token),
      body: { stroke },
    }),
  deleteSwim: (id: number, token: string) =>
    request<undefined>(`/api/activity/swims/${String(id)}`, {
      method: 'DELETE',
      headers: guard(token),
    }),
  /** L'effort perçu d'une nage, sous `If-Match` — ce qui la fait compter dans la charge. */
  setSwimRpe: (id: number, token: string, rpe: number | null) =>
    request<SwimDetail>(`/api/activity/swims/${String(id)}/rpe`, {
      method: 'PUT',
      headers: guard(token),
      body: { rpe },
    }),

  /** Vélo, marche, randonnée (`docs/charge-multisport.md`, D8) : comptés, pas analysés. */
  others: () => request<OtherList>('/api/activity/others'),
  /** Appelé quand l'import de course répond `fit_is_other`, comme pour la nage. */
  importOtherFit: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<Other>('/api/activity/others/fit', { method: 'POST', form });
  },
  /** Corriger une sortie en place, sous `If-Match` : son identifiant stable ne bouge pas. */
  updateOther: (id: number, token: string, payload: OtherCorrection) =>
    request<Other>(`/api/activity/others/${String(id)}`, {
      method: 'PATCH',
      headers: guard(token),
      body: payload,
    }),
  setOtherRpe: (id: number, token: string, rpe: number | null) =>
    request<Other>(`/api/activity/others/${String(id)}/rpe`, {
      method: 'PUT',
      headers: guard(token),
      body: { rpe },
    }),
  deleteOther: (id: number, token: string) =>
    request<undefined>(`/api/activity/others/${String(id)}`, {
      method: 'DELETE',
      headers: guard(token),
    }),
  /** L'analyse, relue depuis le `.fit` rangé : la page s'affiche sans l'attendre. */
  runAnalysis: (id: number) => request<RunAnalysis>(`/api/activity/runs/${id}/analysis`),
  runAdherence: (id: number) => request<RunAdherence>(`/api/activity/runs/${id}/adherence`),
  /** Recalcule efforts et paliers de chaque `.fit` rangé. Rejouable sans rien doubler. */
  rebuildEfforts: () =>
    request<EffortRebuild>('/api/activity/runs/efforts/rebuild', { method: 'POST' }),
  /** L'adresse du fichier rangé. Authentifiée comme toute route de données : un `<a href>`
   *  nu n'y suffit pas, le jeton ne s'attache pas à une navigation. */
  runFitPath: (id: number) => `/api/activity/runs/${id}/fit`,

  circuits: () => request<CircuitList>('/api/activity/circuits'),
  circuitExercises: (query = '') =>
    request<CircuitSuggestion[]>(`/api/activity/circuits/exercises?q=${encodeURIComponent(query)}`),

  loads: () => request<LoadList>('/api/activity/loads'),
  loadDetail: (name: string) =>
    request<LoadDetail>(`/api/activity/loads/detail?name=${encodeURIComponent(name)}`),
  createLoad: (payload: LoadPayload) =>
    request<Load>('/api/activity/loads', { method: 'POST', body: payload }),
  updateLoad: (id: number, token: string, payload: LoadPayload) =>
    request<Load>(`/api/activity/loads/${id}`, {
      method: 'PATCH',
      headers: guard(token),
      body: payload,
    }),
  deleteLoad: (id: number, token: string) =>
    request<undefined>(`/api/activity/loads/${id}`, {
      method: 'DELETE',
      headers: guard(token),
    }),
  createCircuit: (payload: CircuitPayload) =>
    request<Circuit>('/api/activity/circuits', { method: 'POST', body: payload }),
  importCircuit: (url: string) =>
    request<Circuit>('/api/activity/circuits/import', { method: 'POST', body: { url } }),
  updateCircuit: (id: number, token: string, payload: CircuitPayload) =>
    request<Circuit>(`/api/activity/circuits/${id}`, {
      method: 'PATCH',
      headers: guard(token),
      body: payload,
    }),
  deleteCircuit: (id: number, token: string) =>
    request<undefined>(`/api/activity/circuits/${id}`, {
      method: 'DELETE',
      headers: guard(token),
    }),
  /**
   * Une phrase → un circuit **proposé**. N'écrit rien : c'est `createCircuit` qui écrit.
   *
   * Le matériel possédé, les contraintes et les groupes négligés partent avec la demande
   * côté serveur — l'écran n'a rien à leur sujet à envoyer, ni à tenir.
   */
  composeCircuit: (wish: string) =>
    request<CircuitProposal>('/api/activity/circuits/propose', {
      method: 'POST',
      body: { wish },
    }),
  completeCircuit: (id: number, payload: CircuitDonePayload) =>
    request<CircuitSession>(`/api/activity/circuits/${id}/done`, {
      method: 'POST',
      body: payload,
    }),

  /**
   * Supprime une séance tabata **et ses séries**.
   *
   * C'est ce qui autorise « je l'ai fait » à ne rien demander : l'addition se défait par
   * la suppression que l'utilisateur ferait de toute façon. Cadence ne pouvant pas dire à
   * Metric qu'une séance a eu lieu, la déclarer deux fois reste possible.
   */
  deleteSession: (id: number, token: string) =>
    request<undefined>(`/api/activity/sessions/${id}`, {
      method: 'DELETE',
      headers: guard(token),
    }),
};

// ── La natation en piscine (`docs/planning-natation.md` §6) ──

/** Un tour, tel que la montre l'a délimité — et tel qu'on l'a corrigé. */
export interface SwimLap {
  id: number;
  token: string;
  seq: number;
  kind: 'swim' | 'rest';
  distance_m: number;
  duration_s: number;
  idle_s: number;
  /** La nage lue par la montre, longueur par longueur : `['BR', 'CR']`. */
  detected: string[];
  /** La nage corrigée, `null` tant que rien n'a été dit. */
  stroke: string | null;
  strokes: number | null;
  avg_hr: number | null;
  max_hr: number | null;
  /** Une longueur douteuse dans ce tour (§6.2). Signalé, jamais corrigé. */
  suspect: boolean;
}

export interface SwimDetail {
  id: number;
  token: string;
  swim_id: string;
  date: string;
  start: string | null;
  pool_m: number;
  /** La distance de la montre, tours douteux compris (D12) — puis celle des tours, dès qu'on
   *  en a corrigé un (`docs/natation-corriger.md`, D2). */
  distance_m: number;
  duration_s: number;
  elapsed_s: number | null;
  avg_hr: number | null;
  max_hr: number | null;
  strokes: number | null;
  calories: number | null;
  /** Temps **nagé** au 100 m, repos exclus — calculé par le serveur. */
  pace_100_s: number | null;
  suspect_laps: number;
  /** Effort perçu 1–10, noté à part (`docs/charge-multisport.md`, D9). */
  rpe: number | null;
  lines: SwimLine[];
  laps: SwimLap[];
  /** `fit` ou `manual`. Une natation saisie se corrige d'un bloc ; une importée, au tour. */
  source: string;
  /** Le programme tel qu'il a été tapé, pour une natation saisie. */
  program: string | null;
  note: string | null;
  /** Pour une importée : sa lecture tour par tour, dans la notation — le départ d'une
   *  réécriture. `null` pour une saisie. */
  program_hint: string | null;
}

/** Une natation dans une liste — la même, sans ses tours. */
export type Swim = Omit<SwimDetail, 'laps'>;

export interface SwimWeek {
  week: string;
  distance_m: number;
  sessions: number;
}

export interface SwimList {
  swims: Swim[];
  /** Les quatre dernières semaines, la plus récente en dernier — calculées au serveur. */
  weeks: SwimWeek[];
}

export interface SwimPayload {
  date: string;
  start: string | null;
  duration_min: string;
  program: string;
  pool_m: number;
  note: string | null;
  /** Réécrire une natation **importée** : ses tours sont remplacés par le programme. */
  rewrite?: boolean;
}

export type OtherSport = 'vélo' | 'marche' | 'randonnée';

/** Une sortie à vélo, à pied ou en randonnée. */
export interface Other {
  id: number;
  token: string;
  other_id: string;
  date: string;
  start: string | null;
  sport: OtherSport;
  duration_min: number;
  distance_km: number | null;
  avg_hr: number | null;
  max_hr: number | null;
  elevation_m: number | null;
  calories: number | null;
  rpe: number | null;
  source: string;
  note: string | null;
}

/** Un sport que la saisie à la main propose — la liste vient du serveur (M-16). */
export interface SportChoice {
  value: OtherSport;
  label: string;
}

export interface OtherList {
  others: Other[];
  sports: SportChoice[];
}

/** Durée et distance partent en texte : le serveur lit `1h30` et `12,5` comme pour une
 *  course. */
/** Ce que la correction envoie — les champs du formulaire, et eux seuls. */
export interface OtherCorrection {
  date: string;
  sport: OtherSport;
  duration_min: number | string;
  distance_km: number | string | null;
  rpe: number | null;
}
